import Decimal from 'decimal.js';
import type { RuleDefinition, RuleFinding } from '../domain/types';
import { getCellText } from '../domain/cells';
import { decimalMedian, parseNumeric } from '../utils/number';
import { normalizeCode, normalizeTextForComparison, normalizeUnit } from '../utils/text';
import { cellValueForParsing, finding, groupRows, isFormulaWithoutResult } from './helpers';

export const BQ016: RuleDefinition = {
  id: 'BQ016',
  name: '相同清单描述价格差异提醒',
  description: '相同编码、名称、特征和单位至少有三条有效记录时，比较综合单价与中位数的偏离。',
  defaultSeverity: 'info',
  enabled: true,
  applicableFields: ['code', 'name', 'description', 'unit', 'unitPrice'],
  message: '综合单价相对同组中位数偏离超过设置阈值；这不是市场价格合理性判断。',
  suggestion: '请结合计价口径和原始依据人工复核；本提示不构成市场价格或造价合理性结论。',
  check: ({ detailRows, settings }) => {
    const results: RuleFinding[] = [];
    const configuredThreshold = parseNumeric(settings.priceDeviationThreshold);
    const threshold = configuredThreshold.ok
      ? configuredThreshold.decimal.abs()
      : new Decimal('0.5');
    const groups = groupRows(detailRows, (row) => {
      const components = [
        normalizeCode(getCellText(row, 'code')),
        normalizeTextForComparison(getCellText(row, 'name')),
        normalizeTextForComparison(getCellText(row, 'description')),
        normalizeUnit(getCellText(row, 'unit')),
      ];
      return components.some((component) => component.length === 0)
        ? ''
        : components.join('\u001f');
    });

    groups.forEach((rows) => {
      const valid = rows.flatMap((row) => {
        if (isFormulaWithoutResult(row, 'unitPrice')) return [];
        const parsed = parseNumeric(cellValueForParsing(row, 'unitPrice'));
        return parsed.ok ? [{ row, price: parsed.decimal }] : [];
      });
      if (valid.length < 3) return;
      const median = decimalMedian(valid.map(({ price }) => price));
      if (median === null) return;
      valid.forEach(({ row, price }) => {
        const difference = price.minus(median).abs();
        const deviation = median.isZero()
          ? price.isZero()
            ? new Decimal(0)
            : new Decimal(Infinity)
          : difference.div(median.abs());
        if (deviation.lte(threshold)) return;
        results.push(
          finding(row, 'unitPrice', {
            currentValue: getCellText(row, 'unitPrice'),
            relatedRows: valid
              .map(({ row: groupRow }) => groupRow.excelRow)
              .filter((excelRow) => excelRow !== row.excelRow),
            message: `综合单价 ${price.toString()} 相对同组中位数 ${median.toString()} 偏离 ${deviation.isFinite() ? `${deviation.mul(100).toDecimalPlaces(2).toString()}%` : '无法按有限比例表示'}，超过阈值 ${threshold.mul(100).toString()}%；这不是市场价格合理性判断。`,
          }),
        );
      });
    });
    return results;
  },
};
